import React from "react";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  ReplyOutlined,
  Shortcut,
  MoreVert,
  ContentCopy,
  Lock,
  Visibility,
  VisibilityOff,
  PersonAddAltRounded,
} from "@mui/icons-material";
import { IWhatsAppActivity } from "../../../redux/activity/activity.types";
import { ChatLeadsDialog } from "./ChatLeadsDialog";
import { useBillingCompanyContext } from "../../../providers/BillingCompanyProvider";
import { config } from "../../../config";

interface MessageAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}

interface MessageActionsPopoverProps {
  message: IWhatsAppActivity;
  onReply: (message: IWhatsAppActivity) => void;
  onForward?: (message: IWhatsAppActivity) => void;
  onRestrict?: (message: IWhatsAppActivity) => void;
  onShowRestrictedMessage?: (message: IWhatsAppActivity) => void;
  isRestrictedMessageVisible?: boolean;
  isSender: boolean;
}

export const MessageActionsPopover: React.FC<MessageActionsPopoverProps> = ({
  message,
  onReply,
  onForward,
  onRestrict,
  onShowRestrictedMessage,
  isRestrictedMessageVisible = false,
  isSender,
}) => {
  const theme = useTheme();
  const companyOutlet = useBillingCompanyContext();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [confirmChatLead, setConfirmChatLead] = React.useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action: () => void) => {
    action();
    handleClose();
  };

  const handleCopy = (message: IWhatsAppActivity) => {
    const text = message.content?.[0]?.text
      ? message.content?.[0]?.text
      : message.attachments?.[0];

    if (!text) return;
    navigator.clipboard.writeText(text);
  };

  const confirmChatLeads = async () => {
    try {
      const conversationId = message.conversation_id || "";
      const mobileNo = message.sender_phone || message.recipient_number || "";
      const name = message.sender_name || message.recipient_name || "";
      const messageContent = message.content?.[0]?.text || "";

      const payload = {
        conversation_id: conversationId,
        mobile_no: mobileNo,
        name: name,
        description: messageContent,
        source: "WHATSAPP",
        status: "ONGOING",
        actual_status: "ONGOING",
        company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
      };
      const response = await fetch(`${config.api.baseUrl}/conversation/upsert-action-item`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create chat leads");
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error creating chat leads:", error);
      throw error;
    }
  };

  // Build actions array
  const actions: MessageAction[] = [];

  // Reply action (always available)
  actions.push({
    id: "reply",
    label: "Reply",
    icon: <ReplyOutlined fontSize="small" />,
    onClick: () => onReply(message),
  });

  // Forward action (if callback provided)
  if (onForward) {
    actions.push({
      id: "forward",
      label: "Forward",
      icon: <Shortcut fontSize="small" />,
      onClick: () => onForward(message),
    });
  }

  actions.push({
    id: "copy",
    label: "Copy",
    icon: <ContentCopy fontSize="small" />,
    onClick: () => handleCopy(message),
  });
  actions.push({
    id: "chatleads",
    label: "Chat Leads",
    icon: <PersonAddAltRounded fontSize="small" />,
    onClick: () => setConfirmChatLead(true),
  });

  // Restrict action (if callback provided) - marked as destructive
  if (onRestrict) {
    actions.push({
      id: "restrict",
      label: "Restrict Message",
      icon: <Lock fontSize="small" />,
      onClick: () => onRestrict(message),
      destructive: true,
    });
  }

  // Show/Hide restricted message action (only if message is restricted and callback provided)
  const isMessageRestricted =
    message?.is_restricted === 1 ||
    message?.is_restricted === true ||
    Boolean(message?.is_restricted) ||
    (Array.isArray(message.content) &&
      message.content.some(
        (item: any) =>
          item?.is_restricted === 1 ||
          item?.is_restricted === true ||
          Boolean(item?.is_restricted)
      ));

  if (onShowRestrictedMessage && isMessageRestricted) {
    actions.push({
      id: "toggleRestrictedMessage",
      label: isRestrictedMessageVisible
        ? "Hide Restricted Message"
        : "Show Restricted Message",
      icon: isRestrictedMessageVisible ? (
        <VisibilityOff fontSize="small" />
      ) : (
        <Visibility fontSize="small" />
      ),
      onClick: () => onShowRestrictedMessage(message),
      destructive: false,
    });
  }

  // Separate regular and destructive actions
  const regularActions = actions.filter((action) => !action.destructive);
  const destructiveActions = actions.filter((action) => action.destructive);

  return (
    <>
      <Tooltip title="More options">
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{
            width: 24,
            height: 24,
            padding: 0.5,
            color: theme.palette.text.secondary,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          }}
        >
          <MoreVert fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isSender ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: isSender ? "right" : "left",
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 180,
            boxShadow: theme.shadows[8],
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Paper elevation={0}>
          <MenuList dense sx={{ py: 0.5 }}>
            {regularActions.map((action, index) => (
              <React.Fragment key={action.id}>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick(action.onClick);
                  }}
                  sx={{
                    px: 2,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {action.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={action.label}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                    }}
                  />
                </MenuItem>
                {index < regularActions.length - 1 && (
                  <Divider sx={{ my: 0.5 }} />
                )}
              </React.Fragment>
            ))}

            {destructiveActions.length > 0 && regularActions.length > 0 && (
              <Divider sx={{ my: 0.5 }} />
            )}

            {destructiveActions.map((action, index) => (
              <React.Fragment key={action.id}>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick(action.onClick);
                  }}
                  sx={{
                    py: 1,
                    px: 2,
                    color: theme.palette.error.main,
                    "&:hover": {
                      backgroundColor: theme.palette.error.light,
                      color: theme.palette.error.dark,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
                    {action.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={action.label}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
                {index < destructiveActions.length - 1 && (
                  <Divider sx={{ my: 0.5 }} />
                )}
              </React.Fragment>
            ))}
          </MenuList>
        </Paper>
      </Popover>

      <ChatLeadsDialog
        open={confirmChatLead}
        onClose={() => setConfirmChatLead(false)}
        onSave={confirmChatLeads}
      />
    </>
  );
};
